import { useEffect, useState } from "react";
import { ShieldCheck, ShieldOff } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import { Switch } from "../ui/switch";

// Carte « Double vérification (2FA) du personnel » — demande du propriétaire
// (2026-10-20) : choisir PAR COMPTE (vendeur OU admin) si la 2FA est exigée ou
// désactivée. Désactiver éteint aussi une 2FA déjà configurée (le secret est
// effacé — à reconfigurer si la 2FA est ré-exigée) ; le portail de connexion
// saute alors le défi pour ce compte. Visible en haut de Paramètres (admin
// seulement — l'onglet est déjà réservé et le backend impose require_admin).

export default function Staff2FACard() {
  const [staff, setStaff] = useState(null);
  const [busyId, setBusyId] = useState("");

  const charger = async () => {
    try {
      const r = await api.get("/admin/staff-2fa");
      setStaff(r.data.staff || []);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      setStaff([]);
    }
  };
  useEffect(() => { charger(); }, []);

  const basculer = async (m) => {
    const desactiver = !m.twofa_disabled;
    if (desactiver && !window.confirm(
      `Désactiver la double vérification pour ${m.name || m.email} ?\n\n`
      + "Ce compte entrera avec son mot de passe seul. S'il avait déjà configuré "
      + "la 2FA, elle sera effacée (à reconfigurer si vous la ré-exigez plus tard).")) return;
    setBusyId(m.id);
    try {
      await api.patch(`/admin/staff-2fa/${m.id}`, { disabled: desactiver });
      toast.success(desactiver
        ? `2FA désactivée pour ${m.name || m.email} — avis de sécurité envoyé au compte`
        : `2FA de nouveau exigée pour ${m.name || m.email}`);
      charger();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setBusyId(""); }
  };

  if (staff === null) return null;

  return (
    <div className="bg-surface border border-gold/25 rounded-xl p-7 mb-8" data-testid="staff-2fa-card">
      <p className="font-semibold text-gold">Double vérification (2FA) du personnel</p>
      <p className="text-xs text-ivory/70 mt-1 mb-4">
        Choisissez, pour chaque compte vendeur ou administrateur, si la double vérification est
        exigée à la connexion. Désactiver efface la 2FA déjà configurée du compte, et le compte
        reçoit un avis de sécurité par courriel.
      </p>
      <div className="space-y-2">
        {staff.map((m) => {
          const exigee = !m.twofa_disabled;
          return (
            <div key={m.id} className="flex items-center justify-between gap-4 border border-ivory/10 rounded-lg px-4 py-3"
              data-testid={`staff-2fa-${m.id}`}>
              <div className="min-w-0">
                <p className="text-sm font-medium truncate">
                  {m.name || m.email}
                  <span className={`ml-2 text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full ${
                    m.role === "admin" ? "bg-gold/15 text-gold" : "bg-ivory/10 text-ivory/60"}`}>
                    {m.role === "admin" ? "Admin" : "Vendeur"}
                  </span>
                </p>
                <p className="text-xs text-ivory/55 truncate">{m.email}</p>
              </div>
              <div className="flex items-center gap-2.5 shrink-0">
                {exigee ? (
                  <span className="inline-flex items-center gap-1 text-xs text-green-400" data-testid={`staff-2fa-etat-${m.id}`}>
                    <ShieldCheck className="w-3.5 h-3.5" /> {m.totp_enabled ? "Active" : "Exigée (à configurer)"}
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 text-xs text-red-300" data-testid={`staff-2fa-etat-${m.id}`}>
                    <ShieldOff className="w-3.5 h-3.5" /> Désactivée
                  </span>
                )}
                <Switch checked={exigee} disabled={busyId === m.id}
                  onCheckedChange={() => basculer(m)}
                  data-testid={`staff-2fa-toggle-${m.id}`} />
              </div>
            </div>
          );
        })}
      </div>
      <p className="text-[11px] text-ivory/55 mt-3">
        Interrupteur allumé = 2FA exigée (défi quotidien à la connexion) ; éteint = compte exempté.
      </p>
    </div>
  );
}
